import React, { useState, useEffect } from "react";
import {
    TextField,
    Container,
    Box,
    Button,
    Alert,
    Snackbar,
    Grid,
    Typography,
    Paper,
    Avatar
} from "@mui/material";
import { Lock as LockIcon } from "@mui/icons-material";
import AccountService from "~/admin/services/AccountServices";
import { jwtDecode } from "jwt-decode";

const ProfileComp = () => {
    const [account, setAccount] = useState(null);
    const [formData, setFormData] = useState({
        ID: null,
        Username: "",
        Name: "",
        Birthday: "",
        Phone: "",
        Email: "",
        Role: "",
        RoleId: null,
        CurrentPassword: "",
        NewPassword: "",
        ConfirmPassword: ""
    });
    const [alert, setAlert] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const fetchAccountData = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                showAlert("Bạn cần đăng nhập để xem thông tin cá nhân", "error");
                return;
            }

            const decoded = jwtDecode(token);
            const accountId = decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
            const role = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

            if (!accountId) {
                showAlert("Không tìm thấy thông tin tài khoản", "error");
                return;
            }

            const accountData = await AccountService.getById(accountId);
            setAccount(accountData);

            let birthday = "";
            if (accountData.birthday) {
                const dateParts = accountData.birthday.split('T')[0].split('-');
                birthday = `${dateParts[0]}-${dateParts[1]}-${dateParts[2]}`;
            }

            setFormData({
                ID: accountId || null,
                Username: accountData.username || "",
                Name: accountData.name || "",
                Birthday: birthday,
                Phone: accountData.phone || "",
                Email: accountData.email || "",
                Role: role || "",
                RoleId: accountData.roleID || null,
                CurrentPassword: "",
                NewPassword: "",
                ConfirmPassword: ""
            });

            setIsLoading(false);
        } catch (error) {
            showAlert("Lỗi khi tải thông tin tài khoản: " + error.message, "error");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAccountData();
    }, []);

    const showAlert = (message, severity) => {
        setAlert({ open: true, message, severity });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setErrors(prev => ({
            ...prev,
            [name]: ""
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.Name) newErrors.Name = "Tên không được để trống!";
        if (!formData.Birthday) newErrors.Birthday = "Ngày sinh không được để trống!";

        if (!formData.Phone) newErrors.Phone = "Số điện thoại không được để trống!";
        else if (!/^\d{10}$/.test(formData.Phone)) {
            newErrors.Phone = "Số điện thoại phải đủ 10 chữ số!";
        }

        if (!formData.Email) newErrors.Email = "Email không được để trống!";
        else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.Email)) {
            newErrors.Email = "Email không hợp lệ!";
        }

        if (formData.CurrentPassword || formData.NewPassword || formData.ConfirmPassword) {
            if (!formData.CurrentPassword) newErrors.CurrentPassword = "Mật khẩu hiện tại không được để trống!";
            if (!formData.NewPassword) newErrors.NewPassword = "Mật khẩu mới không được để trống!";
            else if (formData.NewPassword.length < 8) newErrors.NewPassword = "Mật khẩu phải có ít nhất 8 ký tự!";

            if (formData.NewPassword !== formData.ConfirmPassword) {
                newErrors.ConfirmPassword = "Mật khẩu xác nhận không khớp!";
            }
        }

        const today = new Date();
        const birthDate = new Date(formData.Birthday);
        const age = today.getFullYear() - birthDate.getFullYear();
        const month = today.getMonth() - birthDate.getMonth();
        if (age < 18 || (age === 18 && month < 0)) {
            newErrors.Birthday = "Bạn phải đủ 18 tuổi!";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async () => {
        if (!validateForm()) return;

        try {
            const updateData = {
                ID: formData.ID,
                Name: formData.Name,
                Birthday: formData.Birthday,
                Phone: formData.Phone,
                Email: formData.Email,
                CurrentPassword: formData.CurrentPassword,
                NewPassword: formData.NewPassword
            };

            if (formData.CurrentPassword && formData.NewPassword) {
                updateData.CurrentPassword = formData.CurrentPassword;
                updateData.NewPassword = formData.NewPassword;
            }

            let response = await AccountService.updateProfile(updateData);

            if (response && response.success === false) {
                showAlert(response.message, "warning");
                return;
            } else {
                showAlert("Cập nhật thông tin thành công!", "success");
            }

            fetchAccountData();
        } catch (error) {
            showAlert(error.message || "Lỗi khi cập nhật thông tin!", "error");
        }
    };

    if (isLoading) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Typography variant="h6">Đang tải thông tin...</Typography>
                </Box>
            </Container>
        );
    }

    if (!account) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Typography variant="h6" color="error">Không tìm thấy thông tin tài khoản</Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Snackbar
                open={alert.open}
                autoHideDuration={3000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })}>
                    {alert.message}
                </Alert>
            </Snackbar>

            <Box sx={{ mt: 4, mb: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                        <Avatar sx={{ width: 100, height: 100, bgcolor: 'primary.main', fontSize: "38px", fontWeight: "bold" }}>
                            {account.name ? account.name.charAt(0).toUpperCase() : 'U'}
                        </Avatar>
                    </Box>

                    <Typography variant="h5" align="center" gutterBottom>
                        Thông tin cá nhân
                    </Typography>

                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={
                                    <span>Tên tài khoản <span style={{ color: "red" }}>*</span></span>
                                }

                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={account.username}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={
                                    <span>Vai trò <span style={{ color: "red" }}>*</span></span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={formData.Role || "N/A"}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={
                                    <span>Tên <span style={{ color: "red" }}>*</span></span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                name="Name"
                                value={formData.Name}
                                onChange={handleInputChange}
                                error={!!errors.Name}
                                helperText={errors.Name}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={
                                    <span>Ngày sinh <span style={{ color: "red" }}>*</span></span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                name="Birthday"
                                type="date"
                                value={formData.Birthday}
                                onChange={handleInputChange}
                                error={!!errors.Birthday}
                                helperText={errors.Birthday}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={
                                    <span>Số điện thoại <span style={{ color: "red" }}>*</span></span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                name="Phone"
                                value={formData.Phone}
                                onChange={handleInputChange}
                                error={!!errors.Phone}
                                helperText={errors.Phone}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={
                                    <span>Email <span style={{ color: "red" }}>*</span></span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                name="Email"
                                value={formData.Email}
                                onChange={handleInputChange}
                                error={!!errors.Email}
                                helperText={errors.Email}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{
                                border: '1px solid #ccc',
                                borderRadius: 1,
                                p: 2,
                                mt: 2,
                                backgroundColor: '#f9f9f9'
                            }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LockIcon sx={{ mr: 1 }} /> Thay đổi mật khẩu
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Để thay đổi mật khẩu, vui lòng nhập mật khẩu hiện tại và mật khẩu mới
                                </Typography>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12}>
                                        <TextField
                                            label={
                                                <span>Mật khẩu hiện tại <span style={{ color: "red" }}>*</span></span>
                                            }
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            name="CurrentPassword"
                                            type="password"
                                            value={formData.CurrentPassword}
                                            onChange={handleInputChange}
                                            error={!!errors.CurrentPassword}
                                            helperText={errors.CurrentPassword}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label={
                                                <span>Mật khẩu mới <span style={{ color: "red" }}>*</span></span>
                                            }
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            name="NewPassword"
                                            type="password"
                                            value={formData.NewPassword}
                                            onChange={handleInputChange}
                                            error={!!errors.NewPassword}
                                            helperText={errors.NewPassword}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label={
                                                <span>Xác nhận mật khẩu mới <span style={{ color: "red" }}>*</span></span>
                                            }
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            name="ConfirmPassword"
                                            type="password"
                                            value={formData.ConfirmPassword}
                                            onChange={handleInputChange}
                                            error={!!errors.ConfirmPassword}
                                            helperText={errors.ConfirmPassword}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleFormSubmit}
                                    size="large"
                                >
                                    Cập nhật
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </Container>
    );
};

export default ProfileComp;